import { useState, useRef, useEffect, useContext } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { getCookie } from 'public/assets/js/analytics/cookie';
import DocsContext from 'src/contexts/docs';

import { BillState } from './BillGraphic';
import { HistoryEntry, Source, Speaker } from './types';
import {
  MAX_HISTORY_LENGTH,
  PRIMER_QUESTIONS,
  INTERNAL_USER_COOKIE_VALUE,
  DEFAULT_FINN_SERVER,
} from './constants';

/**
 * Hook for managing streaming state in the chat interface
 */
export function useStreamingState() {
  const [isStreaming, setIsStreaming] = useState(false);
  const isStreamingRef = useRef(false);

  // Sync ref with state
  useEffect(() => {
    isStreamingRef.current = isStreaming;
  }, [isStreaming]);

  return { isStreaming, setIsStreaming, isStreamingRef };
}

/**
 * Hook for managing UI state in the chat interface
 */
export function useUiState(streamingState: {
  isStreaming: boolean;
  setIsStreaming: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const { isStreaming, setIsStreaming } = streamingState;
  const [lastQuestion, setLastQuestion] = useState('');
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [currentAnswer, setCurrentAnswer] = useState('');
  const [answerFeedback, setAnswerFeedback] = useState('neutral');
  const [answerSources, setAnswerSources] = useState<Array<Source>>([]);
  const [billState, setBillState] = useState<BillState>('empty');
  const [waitingMessage, setWaitingMessage] = useState('');
  const [anyError, setAnyError] = useState(false);
  const [primerQuestions, setPrimerQuestions] = useState<Array<string>>([]);

  useEffect(() => {
    setPrimerQuestions(
      PRIMER_QUESTIONS.sort(() => Math.random() - 0.5).slice(0, 3),
    );
  }, []);

  // Update bill graphic based on chat state
  useEffect(() => {
    const isEmptyState = lastQuestion === '' && currentAnswer === '';
    let newBillState: BillState = 'waiting';

    if (isEmptyState) {
      newBillState = 'empty';
    } else if (currentAnswer === '') {
      newBillState = 'thinking';
    } else if (anyError) {
      newBillState = 'error';
    } else if (isStreaming) {
      newBillState = 'answer';
    }

    setBillState(newBillState);
  }, [lastQuestion, currentAnswer, isStreaming, anyError]);

  // Handle input change in the chat input field
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentQuestion((e?.target as HTMLInputElement)?.value);
  };

  // Check if the chat is in empty state
  const isEmptyState = () => {
    return lastQuestion === '' && currentAnswer === '';
  };

  return {
    lastQuestion,
    setLastQuestion,
    currentQuestion,
    setCurrentQuestion,
    currentAnswer,
    setCurrentAnswer,
    answerFeedback,
    setAnswerFeedback,
    answerSources,
    setAnswerSources,
    billState,
    waitingMessage,
    setWaitingMessage,
    anyError,
    setAnyError,
    primerQuestions,
    handleInputChange,
    isEmptyState,
    // Include isStreaming state in the UI hook return value
    isStreaming,
    setIsStreaming,
  };
}

/**
 * Hook for managing WebSocket state and connections
 */
export function useWebSocketState(streamingState: {
  isStreamingRef: React.MutableRefObject<boolean>;
}) {
  const { isStreamingRef } = streamingState;
  const [finnServer, setFinnServer] = useState(DEFAULT_FINN_SERVER);
  const [chatHistory, setChatHistory] = useState<HistoryEntry[]>([]);

  const finnServerRef = useRef(DEFAULT_FINN_SERVER);
  const currentWebsocket = useRef<WebSocket | null>(null);
  const pendingQuestion = useRef(null);
  const hasStreamingStarted = useRef(false);
  const lastQuestionID = useRef('');
  const addAnswerToHistory = useRef(() => {});
  const timeQuestionSubmitted = useRef(0);

  // Update server ref when server changes
  useEffect(() => {
    finnServerRef.current = finnServer;
  }, [finnServer]);

  // Generate a unique ID for a new question
  const generateQuestionId = () => {
    lastQuestionID.current = uuidv4();
    return lastQuestionID.current;
  };

  // Add an item to the chat history
  const addItemToHistory = (speaker: Speaker, content: string) => {
    setChatHistory((prevHistory) => {
      const newHistory = [...prevHistory, { speaker, content }];
      if (newHistory.length > MAX_HISTORY_LENGTH) {
        newHistory.splice(0, newHistory.length - MAX_HISTORY_LENGTH);
      }
      return newHistory;
    });
  };

  return {
    isStreamingRef,
    finnServer,
    setFinnServer,
    finnServerRef,
    chatHistory,
    setChatHistory,
    currentWebsocket,
    pendingQuestion,
    hasStreamingStarted,
    lastQuestionID,
    addAnswerToHistory,
    timeQuestionSubmitted,
    addItemToHistory,
    generateQuestionId,
  };
}

/**
 * Hook for managing user state
 */
export function useUserState() {
  const { isLoggedIn, user } = useContext(DocsContext);

  const userId = useRef('');
  const anonId = useRef('');
  const likelyInternal = useRef(false);

  // Initialize user info
  useEffect(() => {
    if (isLoggedIn) {
      userId.current = user.userId;
    }
    anonId.current = window.localStorage.getItem('ajs_anonymous_id') ?? '';
  }, [isLoggedIn, user]);

  // Check if user is internal
  useEffect(() => {
    if (getCookie('likely-internal-user') === INTERNAL_USER_COOKIE_VALUE) {
      likelyInternal.current = true;
    }
  }, []);

  return {
    userId,
    anonId,
    likelyInternal,
  };
}

/**
 * Hook for managing timeout state
 */
export function useTimeoutState() {
  // Timeout refs
  const smallWaitTimeoutId = useRef<NodeJS.Timeout | null>(null);
  const patienceTimeoutId = useRef<NodeJS.Timeout | null>(null);
  const serviceTimeoutId = useRef<NodeJS.Timeout | null>(null);

  // Reset timeout IDs
  const resetTimeouts = () => {
    if (smallWaitTimeoutId.current) clearTimeout(smallWaitTimeoutId.current);
    if (patienceTimeoutId.current) clearTimeout(patienceTimeoutId.current);
    if (serviceTimeoutId.current) clearTimeout(serviceTimeoutId.current);
  };

  return {
    smallWaitTimeoutId,
    patienceTimeoutId,
    serviceTimeoutId,
    resetTimeouts,
  };
}

/**
 * Main chat state hook that composes all the specialized hooks
 */
export function useChatState() {
  // Create a single instance of streaming state that will be shared
  const streamingState = useStreamingState();

  // Pass streaming state to the hooks that need it
  const uiState = useUiState(streamingState);
  const webSocketState = useWebSocketState(streamingState);
  const userState = useUserState();
  const timeoutState = useTimeoutState();

  // Update addAnswerToHistory ref when currentAnswer changes
  useEffect(() => {
    webSocketState.addAnswerToHistory.current = () => {
      webSocketState.addItemToHistory('chatbot', uiState.currentAnswer);
    };
  }, [uiState.currentAnswer, webSocketState]);

  return {
    ...uiState,
    ...webSocketState,
    ...userState,
    ...timeoutState,
  };
}
